import { mapState } from 'pinia'

import Popover from 'src/components/popover/popover.vue'
import UserListPopover from 'src/components/user_list_popover/user_list_popover.vue'
import ActionButtonContainer from './action_button_container.vue'
import { BUTTONS } from './buttons_definitions.js'

import { useStatusesStore } from 'src/stores/statuses.js'

// favorite and the emoji picker: pinned, they are chips beside the
// reactions, not buttons in the row; unpinned, they are in the menu.
// While the pins are edited they are buttons, to be unpinned like the rest.
const CHIPS = ['favorite', 'emoji']

import { useInstanceCapabilitiesStore } from 'src/stores/instance_capabilities.js'
import { useSyncConfigStore } from 'src/stores/sync_config.js'
import { useUsersStore } from 'src/stores/users.js'

import { confirm } from 'src/services/confirm/confirm.js'
import genRandomSeed from 'src/services/random_seed/random_seed.service.js'

const StatusActionButtons = {
  props: {
    status: {
      type: Object,
      required: true,
    },
    replying: {
      type: Boolean,
      default: false,
    },
    quoting: {
      type: Boolean,
      default: false,
    },
    // who favorited and who repeated, for the counts' popovers
    favoritedBy: Set,
    repeatedBy: Set,
  },
  emits: ['toggleReplying', 'toggleQuoting', 'onSuccess', 'onError'],
  data() {
    return {
      showPin: false,
      participantsOpen: false,
      participantCategory: 'favorite',
      randomSeed: genRandomSeed(),
      emojiPickerShown: false,
    }
  },
  components: {
    Popover,
    UserListPopover,
    ActionButtonContainer,
  },
  computed: {
    ...mapState(useSyncConfigStore, {
      userPinnedItems: (store) =>
        new Set(store.prefsStorage.collections.pinnedStatusActions),
    }),
    participantCategories() {
      return [
        ...BUTTONS.filter(
          (b) =>
            b.people && (!b.peopleAvailable || b.peopleAvailable(this.funcArg)),
        ).map((b) => ({
          name: b.name,
          label: this.$t(
            `tool_tip.${b.name === 'retweet' ? 'repeat' : b.name}`,
          ),
          ids: b.people(this.funcArg),
          load: () => b.loadPeople(this.funcArg),
          hasMore: b.hasMorePeople?.(this.funcArg),
          loadMore: b.loadMorePeople
            ? () => b.loadMorePeople(this.funcArg)
            : undefined,
        })),
        ...(this.status.emoji_reactions || []).map((r) => ({
          name: `emoji:${r.name}`,
          label: r.name,
          ids:
            r.account_ids || r.accounts
              ? new Set(r.account_ids ?? r.accounts.map((u) => u.id))
              : undefined,
          load: () =>
            useStatusesStore().fetchEmojiReactions(this.status.id, {
              listOnly: true,
            }),
        })),
      ]
    },
    selectedParticipants() {
      return (
        this.participantCategories.find(
          (c) => c.name === this.participantCategory,
        ) || this.participantCategories[0]
      )
    },
    buttons() {
      return BUTTONS.filter((x) => (x.if ? x.if(this.funcArg) : true))
    },
    pinnedButtons() {
      return this.buttons.filter((x) => this.userPinnedItems.has(x.name))
    },
    quickButtons() {
      if (this.showPin) return this.pinnedButtons
      return this.pinnedButtons.filter((x) => !CHIPS.includes(x.name))
    },
    extraButtons() {
      return this.buttons.filter((x) => !this.userPinnedItems.has(x.name))
    },
    // { favorite, emoji }: each button, or undefined where it is not a chip
    chips() {
      if (this.showPin) return {}
      return Object.fromEntries(
        CHIPS.map((name) => [
          name,
          this.pinnedButtons.find((x) => x.name === name),
        ]),
      )
    },
    // what every button outside the "···" menu gets
    rowProps() {
      return {
        status: this.status,
        extra: false,
        funcArg: this.funcArg,
        getComponent: this.getComponent,
        doAction: this.doAction,
        onEmojiPickerShown: this.onEmojiPickerShown,
      }
    },
    currentUser() {
      return useUsersStore().currentUser
    },
    funcArg() {
      return {
        status: this.status,
        replying: this.replying,
        quoting: this.quoting,
        favoritedBy: this.favoritedBy,
        repeatedBy: this.repeatedBy,
        quotingAvailable: useInstanceCapabilitiesStore().quotingAvailable,
        emojiPickerShown: this.emojiPickerShown,
        emit: this.$emit,
        router: this.$router,
        currentUser: this.currentUser,
        loggedIn: !!this.currentUser,
      }
    },
    triggerAttrs() {
      return {
        title: this.$t('status.more_actions'),
        'aria-controls': `popup-menu-${this.randomSeed}`,
        'aria-haspopup': 'menu',
      }
    },
  },
  methods: {
    async closeParticipants() {
      this.participantsOpen = false
      await this.$nextTick()
      this.$refs.moreActions.$refs.trigger.focus()
    },
    // a button that asks first (deleting, repeating: see its definition)
    async doAction(button) {
      if (button.confirm?.(this.funcArg)) {
        const strings = button.confirmStrings(this.funcArg)
        const yes = await confirm({
          title: this.$t(strings.title),
          text: this.$t(strings.body),
          confirmText: this.$t(strings.confirm),
          cancelText: this.$t(strings.cancel),
          danger: !!button.confirmDanger,
        })
        if (!yes) return
      }
      this.doActionReal(button)
    },
    doActionReal(button) {
      const promise = button.action?.(this.funcArg) ?? Promise.resolve()
      promise
        .then(() => this.$emit('onSuccess'))
        .catch((err) => this.$emit('onError', err))
    },
    onExtraClose() {
      this.showPin = false
    },
    onEmojiPickerShown(state) {
      this.emojiPickerShown = state
    },
    unpin(button) {
      useSyncConfigStore().removeCollectionPreference({
        path: 'collections.pinnedStatusActions',
        value: button.name,
      })
      useSyncConfigStore().pushSyncConfig()
    },
    pin(button) {
      useSyncConfigStore().addCollectionPreference({
        path: 'collections.pinnedStatusActions',
        value: button.name,
      })
      useSyncConfigStore().pushSyncConfig()
    },
    getComponent(button) {
      if (!useUsersStore().currentUser && button.anonLink) {
        return 'a'
      } else if (button.action == null && button.link != null) {
        return 'a'
      } else {
        return 'button'
      }
    },
    getClass(button) {
      return {
        [button.name + '-button']: true,
        disabled: button.interactive
          ? !button.interactive(this.funcArg)
          : false,
        '-pin-edit': this.showPin,
        '-dropdown': button.dropdown?.(),
        '-active': button.active?.(this.funcArg),
      }
    },
  },
}

export default StatusActionButtons
